/**
 * components/wishlist/context-menu-sheet.tsx
 *
 * Bottom sheet context menu revealed on long-press.
 * Per Section 12.4 of design-language.md.
 * Shows: nudge (partner's items), edit (your items), mark done (both), delete (your items).
 */
import type { WishlistItem } from '@just-us/shared'

interface ContextMenuSheetProps {
  item: WishlistItem
  partnerNickname: string
  isNudging: boolean
  isDeletingItem: boolean
  onNudge: (item: WishlistItem) => void
  onEdit: (item: WishlistItem) => void
  onMarkDone: (item: WishlistItem) => void
  onDelete: (item: WishlistItem) => void
  onClose: () => void
}

const isWithin24h = (ts: number) => Date.now() - ts * 1000 < 86_400_000

const hoursUntilNudgeReset = (nudgedAt: number): number => {
  const resetAt = nudgedAt * 1000 + 86_400_000
  return Math.ceil((resetAt - Date.now()) / 3_600_000)
}

export default function ContextMenuSheet({
  item,
  partnerNickname,
  isNudging,
  isDeletingItem,
  onNudge,
  onEdit,
  onMarkDone,
  onDelete,
  onClose,
}: ContextMenuSheetProps) {
  const nudgeBlocked = !!(item.nudgedAt && isWithin24h(item.nudgedAt))

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-[60] animate-fade-in"
        onClick={onClose}
      />

      {/* Sheet */}
      <div className="fixed bottom-0 left-0 right-0 max-w-[430px] mx-auto z-[70] bg-white rounded-t-[24px] shadow-[0_-4px_40px_rgba(0,0,0,0.12)] animate-slide-up pb-[env(safe-area-inset-bottom)]">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-[36px] h-[4px] rounded-full bg-[#F4C0D1]" />
        </div>

        {/* Item title header */}
        <div className="px-5 py-3 border-b border-[#F9E0E8]">
          <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] truncate">{item.title}</p>
        </div>

        <div className="px-5 py-2">
          {/* Nudge — only for partner's items */}
          {!item.isAddedByMe && (
            <button
              type="button"
              disabled={nudgeBlocked || isNudging}
              onClick={() => { onNudge(item); onClose() }}
              className="
                flex items-center gap-3 w-full h-[52px]
                font-['DM_Sans'] text-[15px] text-[#2C1810]
                active:bg-[#FBEAF0] rounded-[10px] px-3
                transition-colors duration-100 touch-manipulation
                disabled:opacity-50
              "
            >
              <span className="text-[20px]">👀</span>
              <div className="text-left">
                <span className="block">Nudge {partnerNickname}</span>
                {nudgeBlocked && item.nudgedAt && (
                  <span className="font-['DM_Sans'] text-[11px] text-[#B89AA2]">
                    Already nudged · available in {hoursUntilNudgeReset(item.nudgedAt)}h
                  </span>
                )}
              </div>
            </button>
          )}

          {/* Edit — only your own items */}
          {item.isAddedByMe && (
            <button
              type="button"
              onClick={() => { onEdit(item); onClose() }}
              className="
                flex items-center gap-3 w-full h-[52px]
                font-['DM_Sans'] text-[15px] text-[#2C1810]
                active:bg-[#FBEAF0] rounded-[10px] px-3
                transition-colors duration-100 touch-manipulation
              "
            >
              <span className="text-[20px]">✏️</span>
              Edit item
            </button>
          )}

          {/* Mark done — both can do it */}
          {!item.doneAt && (
            <button
              type="button"
              onClick={() => { onMarkDone(item); onClose() }}
              className="
                flex items-center gap-3 w-full h-[52px]
                font-['DM_Sans'] text-[15px] text-[#085041]
                active:bg-[#E1F5EE] rounded-[10px] px-3
                transition-colors duration-100 touch-manipulation
              "
            >
              <span className="text-[20px]">✅</span>
              Mark as done together
            </button>
          )}

          {/* Delete — only your own items */}
          {item.isAddedByMe && (
            <button
              type="button"
              disabled={isDeletingItem}
              onClick={() => { onDelete(item); onClose() }}
              className="
                flex items-center gap-3 w-full h-[52px]
                font-['DM_Sans'] text-[15px] text-[#C0392B]
                active:bg-[#FCEBEB] rounded-[10px] px-3
                transition-colors duration-100 touch-manipulation
                disabled:opacity-50
              "
            >
              <span className="text-[20px]">🗑️</span>
              Delete
            </button>
          )}
        </div>

        {/* Cancel */}
        <div className="px-5 pb-4">
          <button
            type="button"
            onClick={onClose}
            className="
              w-full h-[48px] rounded-[14px]
              bg-[#F9E0E8] border border-[#F4C0D1]
              font-['DM_Sans'] text-[15px] font-medium text-[#D4537E]
              active:bg-[#FBEAF0] transition-colors duration-100 touch-manipulation
            "
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  )
}
